import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { bulkOpenSubRequests, getAdminSession, getAdminSessions } from '@/lib/api-calls.js';
import { pluralize } from '@/lib/string-utils.js';
import {
  FrequentTimeZones,
  formatShortDateWithDayOfWeek,
  formatTimeRange,
  getAdminOpenSubRequestEligibility,
  partitionBy,
} from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo, useState } from 'react';
import { z } from 'zod';

export interface BulkOpenSubRequestsProps {
  sessions: AdminSession[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({});

type IneligibleSession = {
  session: AdminSession;
  reasonLabel: string;
};

function formatSessionDateTimeCt(session: AdminSession): string {
  const timeZone = FrequentTimeZones.UsCentral;
  const date = formatShortDateWithDayOfWeek(session.startInstant, timeZone);
  const time = formatTimeRange(session.startInstant, session.endInstant, timeZone);
  return `${date} · ${time}`;
}

function partitionSessionsForOpenSubRequests(sessions: AdminSession[]) {
  const [eligibleSessions, ineligibleRaw] = partitionBy(sessions, (session) => {
    return getAdminOpenSubRequestEligibility(session).eligible;
  });

  const ineligibleSessions: IneligibleSession[] = ineligibleRaw.map((session) => {
    const eligibility = getAdminOpenSubRequestEligibility(session);
    return {
      session,
      reasonLabel: eligibility.eligible ? '' : eligibility.reasonLabel,
    };
  });

  return { eligibleSessions, ineligibleSessions };
}

export const BulkOpenSubRequests = ({ sessions, trigger, onSuccess }: BulkOpenSubRequestsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: bulkOpenSubRequests,
  });

  // Snapshot selection when the modal opens so background refetches cannot change counts mid-dialog.
  const [sessionsAtOpen, setSessionsAtOpen] = useState<AdminSession[]>(sessions);

  const { eligibleSessions, ineligibleSessions } = useMemo(
    () => partitionSessionsForOpenSubRequests(sessionsAtOpen),
    [sessionsAtOpen],
  );

  const eligibleCount = eligibleSessions.length;
  const ineligibleCount = ineligibleSessions.length;
  const noEligibleSessions = eligibleCount === 0;

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({ sessionIds: eligibleSessions.map((session) => session.id) })
      .then(({ successSessionIds, errors, failedSessionIds }) => {
        if (successSessionIds.length > 0) {
          toast({
            title: pluralize(
              successSessionIds.length,
              '1 sub request opened',
              `${successSessionIds.length} sub requests opened`,
            ),
          });
        }

        // Include modal-excluded sessions plus any that failed revalidation on submit.
        const skippedCount = ineligibleCount + failedSessionIds.length;
        if (skippedCount > 0) {
          toast({
            title: pluralize(skippedCount, '1 session skipped', `${skippedCount} sessions skipped`),
          });
        }

        if (errors.length > 0) {
          toast({
            title: 'Errors',
            variant: 'destructive',
            duration: 15000,
            description: (
              <ul>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ),
          });
        }

        queryClient.invalidateQueries(getAdminSessions());
        for (const id of successSessionIds) {
          queryClient.invalidateQueries(getAdminSession(id));
        }

        closeModal();

        if (successSessionIds.length > 0) {
          onSuccess?.();
        }
      })
      .catch(() => {
        toast({ title: 'Failed to open sub requests', variant: 'destructive' });
      });
  };

  return (
    <FormModal
      modalTitle="Open sub requests"
      dialogContentProps={{ className: 'max-w-lg lg:max-w-2xl' }}
      formSchema={formSchema}
      trigger={trigger}
      dataTestId="sessions-future-dialog-bulk-open-sub-requests"
      onOpenChange={(open) => {
        if (open) {
          setSessionsAtOpen(sessions);
        }
      }}
      onSubmit={noEligibleSessions ? undefined : onSubmit}
      submitButtonProps={{
        disabled: noEligibleSessions || isPending,
        loading: isPending,
        variant: 'destructive',
      }}
      confirmBtnLabel={
        eligibleCount === 0
          ? 'Open sub requests'
          : pluralize(eligibleCount, 'Open 1 sub request', `Open ${eligibleCount} sub requests`)
      }
    >
      {() => (
        <div className="my-6 space-y-4">
          {noEligibleSessions ? (
            <p className="text-sm">
              None of the selected sessions are eligible for a new sub request. Review the skipped sessions below for
              details.
            </p>
          ) : (
            <p className="text-sm">
              You will open sub requests for {eligibleCount} eligible {pluralize(eligibleCount, 'session', 'sessions')}.
              Each session will create a separate request and follow the existing substitute notification process.
            </p>
          )}

          <ul className="list-disc space-y-1 pl-5">
            {!noEligibleSessions && (
              <li>
                {pluralize(
                  eligibleCount,
                  '1 sub request will be opened',
                  `${eligibleCount} sub requests will be opened`,
                )}
              </li>
            )}
            {ineligibleCount > 0 && (
              <li>
                {pluralize(ineligibleCount, '1 session will be skipped', `${ineligibleCount} sessions will be skipped`)}
              </li>
            )}
          </ul>

          {ineligibleCount > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">
                {pluralize(ineligibleCount, 'Session that will be skipped', 'Sessions that will be skipped')}
              </h3>
              <ul className="space-y-3 ml-3">
                {ineligibleSessions.map(({ session, reasonLabel }) => (
                  <li key={session.id}>
                    <div>{formatSessionDateTimeCt(session)}</div>
                    <div className="text-sm text-muted-foreground">{reasonLabel}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
};
